"use client";

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Layers,
  Video,
  FileQuestion,
  GraduationCap,
  Users,
  CreditCard,
  CalendarDays,
  MessageSquare,
  Settings,
  Mic,
  Coins,
  Activity,
  Banknote,
  MessageCircleQuestion,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Bell,
} from "lucide-react";

const navItems = [
  { label: "Dashboard", href: "/", icon: LayoutDashboard },
  { label: "Levels (A1→C1)", href: "/levels", icon: Layers, badge: "6" },
  { label: "Video Lessons", href: "/lessons", icon: Video },
  { label: "AI Exercises", href: "/exercises", icon: FileQuestion },
  { label: "Goethe Exams", href: "/exams", icon: GraduationCap },
  { label: "Speaking & Exam", href: "/speaking", icon: Mic },
  { label: "Learners & Access", href: "/learners", icon: Users },
  { label: "Subscriptions", href: "/subscriptions", icon: CreditCard },
  { label: "AI Credits", href: "/credits", icon: Coins },
  { label: "AI Cost & Ops", href: "/ai-cost", icon: Activity },
  { label: "AI Model & Pricing", href: "/ai-model-pricing", icon: Sparkles },
  { label: "Finance", href: "/finance", icon: Banknote },
  { label: "1-on-1 Bookings", href: "/bookings", icon: CalendarDays, badge: "Soon" },
  { label: "WhatsApp Hotline", href: "/whatsapp", icon: MessageSquare, badge: "Soon" },
  { label: "Platform Config", href: "/config", icon: Settings, hasAuditDot: true },
  { label: "FAQ", href: "/faq", icon: MessageCircleQuestion, hasAuditDot: false },
  { label: "Notifications", href: "/notifications", icon: Bell },
];

export function Sidebar({
  isCollapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
}: {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}) {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const [auditDotCleared, setAuditDotCleared] = useState(false);

  const handleLogout = () => {
    navigate("/login");
  };

  const sidebarContent = (
    <div className="h-full flex flex-col justify-between bg-[#0A0A0A] border-r border-[rgba(255,255,255,0.06)] relative select-none">
      {/* Top Header */}
      <div>
        <div className="h-20 flex items-center justify-between px-5 border-b border-[rgba(255,255,255,0.06)]">
          <Link to="/" className="flex items-center gap-3 overflow-hidden">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-xl bg-[#E63946] flex items-center justify-center font-black text-[#FAFAFA] text-lg shadow-md shrink-0"
            >
              FG
            </motion.div>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="flex flex-col overflow-hidden whitespace-nowrap"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-base tracking-tight text-[#FAFAFA]">
                      FullGerman
                    </span>
                    <span className="font-mono text-[10px] font-black bg-[#FFD60A] text-[#0A0A0A] px-1.5 py-0.5 rounded-full uppercase">
                      Admin
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-[#8E8E93] truncate">
                    klaus@fullgerman.de
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </Link>

          {/* Collapse Toggle for Desktop */}
          <button
            onClick={onToggleCollapse}
            className="hidden md:flex p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#8E8E93] hover:text-[#FAFAFA] transition-colors"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation List */}
        <nav className="py-4 space-y-1 overflow-y-auto max-h-[calc(100vh-160px)] px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            const isConfig = item.label === "Platform Config";
            const showPulse = item.hasAuditDot && !auditDotCleared;

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => {
                  if (isConfig) setAuditDotCleared(true);
                  if (mobileOpen) onCloseMobile();
                }}
                className={`group relative flex items-center h-11 rounded-[10px] mx-1 transition-all duration-200 ${
                  isActive
                    ? "bg-[rgba(255,214,10,0.1)] text-[#FFD60A] pl-4 font-bold"
                    : "text-[#8E8E93] hover:bg-[rgba(255,255,255,0.05)] hover:text-[#FAFAFA] pl-3.5 font-medium"
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                {/* Active Left Border Bar */}
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active-bar"
                    className="absolute left-0 top-2 bottom-2 w-[3px] bg-[#FFD60A] rounded-r"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}

                <div className="flex items-center gap-3.5 min-w-0 flex-1">
                  <div className="relative shrink-0">
                    <Icon
                      className={`w-5 h-5 transition-transform duration-200 ${
                        isActive
                          ? "text-[#FFD60A] scale-110 opacity-100"
                          : "opacity-60 group-hover:opacity-100 group-hover:scale-105"
                      }`}
                    />
                    {showPulse && (
                      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD60A] opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FFD60A]" />
                      </span>
                    )}
                  </div>

                  <AnimatePresence>
                    {!isCollapsed && (
                      <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: "auto" }}
                        exit={{ opacity: 0, width: 0 }}
                        className="flex items-center justify-between flex-1 min-w-0 overflow-hidden pr-2 whitespace-nowrap"
                      >
                        <span className="truncate text-xs tracking-tight">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`font-mono text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-bold shrink-0 ${
                              item.badge === "Soon"
                                ? "bg-white/10 text-[#8E8E93]"
                                : "bg-[#FFD60A]/20 text-[#FFD60A]"
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* User Footer Section */}
      <div className="p-4 border-t border-[rgba(255,255,255,0.06)] bg-[#101010]/50">
        <div className="flex items-center justify-between gap-2 overflow-hidden">
          <div className="flex items-center gap-2.5 min-w-0 overflow-hidden">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#FFD60A] flex items-center justify-center font-bold text-xs text-[#0A0A0A] shrink-0">
              KA
            </div>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="flex flex-col min-w-0 overflow-hidden whitespace-nowrap"
                >
                  <span className="text-xs font-bold text-[#FAFAFA] truncate">
                    Klaus Adminowski
                  </span>
                  <span className="text-[10px] font-mono text-[#8E8E93] truncate">
                    klaus@fullgerman.de
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={handleLogout}
            className="p-2 rounded-lg text-[#E63946] hover:bg-[#E63946]/10 transition-colors group shrink-0"
            title="Log out"
          >
            <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar Rail */}
      <motion.aside
        layout
        initial={false}
        animate={{ width: isCollapsed ? 72 : 260 }}
        transition={{ type: "spring", stiffness: 450, damping: 32 }}
        className="hidden md:block shrink-0 h-screen sticky top-0 z-40 overflow-hidden"
      >
        {sidebarContent}
      </motion.aside>

      {/* Mobile Sidebar Drawer Header Button */}
      <div className="md:hidden sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[rgba(255,255,255,0.08)] px-4 h-16 flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-xl bg-[#1C1C1E] border border-white/10 text-[#FAFAFA]"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#E63946] flex items-center justify-center font-black text-xs text-[#FAFAFA]">
              FG
            </div>
            <span className="font-bold text-sm tracking-tight text-[#FAFAFA]">FullGerman Admin</span>
          </Link>
        </div>

        <div className="flex items-center gap-2 font-mono text-xs font-bold bg-[#FFD60A] text-[#0A0A0A] px-2.5 py-1 rounded-full">
          <span>A1→C1</span>
        </div>
      </div>

      {/* Mobile Slide-in Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onCloseMobile}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            />

            {/* Panel */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="relative w-[280px] h-full z-10"
            >
              <button
                onClick={onCloseMobile}
                className="absolute top-4 right-4 z-20 p-2 rounded-lg bg-white/10 text-[#FAFAFA]"
              >
                <X className="w-5 h-5" />
              </button>
              {sidebarContent}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
